// Device Settings — the hub opened from a device card's ⋮ menu. Step 1: rename
// the device and each button/output. Wi-Fi and button-properties tabs are added
// next. Endpoints: UpdateDeviceInstalledByUniqueNumber (DeviceName),
// UpdateOutputDetailsByUniqueNumberAndOutput (OutputName).

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack,
  Typography, CircularProgress, Divider, Box, Alert, Tabs, Tab,
} from '@mui/material';
import { getOutputs, updateDeviceName, updateOutputDetail } from '../../../core/api/api';
import { deviceLabel } from '../../../core/devices/deviceMeta';
import DeviceWifiPanel from './DeviceWifiPanel';
import ButtonSettingsPanel from './ButtonSettingsPanel';
import ChildLockPanel from '../../shared/ChildLockPanel';
import { supportsChildLock } from '../../../core/devices/childLock';

export default function DeviceSettingsDialog({ device, open, onClose, onSaved }) {
  const uno = device.UniqueNumber;
  const [name, setName] = useState(device.DeviceName || '');
  const [outputs, setOutputs] = useState([]);      // [{ Output, OutputName }]
  const [names, setNames] = useState({});          // { output: name }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    setName(device.DeviceName || '');
    setMsg(null);
    let alive = true;
    setLoading(true);
    getOutputs(uno)
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res.data) ? res.data : [];
        list.sort((a, b) => Number(a.Output) - Number(b.Output));
        setOutputs(list);
        const nm = {};
        list.forEach((o) => { nm[o.Output] = o.OutputName || ''; });
        setNames(nm);
      })
      .catch(() => setOutputs([]))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, uno, device.DeviceName]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const calls = [];
      if (name.trim() && name.trim() !== (device.DeviceName || '')) {
        calls.push(updateDeviceName(uno, name.trim()));
      }
      outputs.forEach((o) => {
        const nv = (names[o.Output] || '').trim();
        if (nv && nv !== (o.OutputName || '')) {
          calls.push(updateOutputDetail(uno, o.Output, { OutputName: nv }));
        }
      });
      await Promise.all(calls);
      setMsg({ type: 'success', text: 'Saved.' });
      if (onSaved) onSaved({ ...device, DeviceName: name.trim() || device.DeviceName });
    } catch (e) {
      setMsg({ type: 'error', text: 'Save failed — check your connection.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>Device Settings</DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, minHeight: 40 }}>
        <Tab label="General" sx={{ minHeight: 40 }} />
        <Tab label="Buttons" sx={{ minHeight: 40 }} />
        <Tab label="Wi‑Fi" sx={{ minHeight: 40 }} />
      </Tabs>
      <DialogContent dividers>
        {tab === 2 ? (
          <DeviceWifiPanel device={device} />
        ) : tab === 1 ? (
          <ButtonSettingsPanel device={device} />
        ) : (
        <>
        {msg && <Alert severity={msg.type} sx={{ mb: 1.5 }}>{msg.text}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="Device name" value={name} fullWidth size="small"
            onChange={(e) => setName(e.target.value)}
            helperText={`${deviceLabel(device.DeviceID)} · ${uno}`}
          />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Buttons</Typography>
            <Divider sx={{ mb: 1 }} />
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
            ) : outputs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">This device has no named buttons.</Typography>
            ) : (
              <Stack spacing={1.25}>
                {outputs.map((o) => (
                  <TextField
                    key={o.Output} size="small" fullWidth
                    label={`Button ${o.Output}`}
                    value={names[o.Output] ?? ''}
                    onChange={(e) => setNames((n) => ({ ...n, [o.Output]: e.target.value }))}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {/* Touch panels only — a device with no keypad has nothing to lock.
              See core/devices/childLock.js for the type list and for why the
              4+1 is deliberately not in it. */}
          {supportsChildLock(device.DeviceID) && <ChildLockPanel device={device} />}
        </Stack>
        </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {tab === 0 && (
          <Button variant="contained" onClick={save} disabled={saving || loading}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
